import i18n from "@/i18n";
import React from "react";
import { AlertTriangle } from "lucide-react";

// R5 · R6 — manager-facing warnings about whether the stored Close's
// Averaging result is still valid. Read-only; never changes the Close.
const STATUS_TEXT = {
    get PENDING() { return i18n.t("wf.AveragingCloseValidity.STATUS_TEXT.PENDING", "Averaging result is pending — some hours, days or absences up to the cycle end still need review"); },
    get AFFECTED() { return i18n.t("wf.AveragingCloseValidity.STATUS_TEXT.AFFECTED", "Averaging balance stored in this Close is incorrect (former balance defect)"); },
    get UNVERIFIED() { return i18n.t("wf.AveragingCloseValidity.STATUS_TEXT.UNVERIFIED", "Averaging balance stored in this Close cannot be verified"); },
    get NOT_CURRENT() { return i18n.t("wf.AveragingCloseValidity.STATUS_TEXT.NOT_CURRENT", "Averaging result is no longer current — a source period changed after this Close"); },
};
const ACTION_TEXT = {
    get REOPEN_AND_RECLOSE() { return i18n.t("wf.AveragingCloseValidity.ACTION_TEXT.REOPEN_AND_RECLOSE", "Reopen the period and Close it again to store a reconciled result."); },
    get CONFIRM_HOURS_THROUGH_CYCLE_END() { return i18n.t("wf.AveragingCloseValidity.ACTION_TEXT.CONFIRM_HOURS_THROUGH_CYCLE_END", "Confirm the hours listed in the cycle result below. No other restaurant needs to close its period."); },
    get WAIT_FOR_SOURCE_PERIODS_TO_CLOSE() { return i18n.t("wf.AveragingCloseValidity.ACTION_TEXT.WAIT_FOR_SOURCE_PERIODS_TO_CLOSE", "Resolve the items listed in the cycle result below. No other restaurant needs to close its period."); },
};

// Block A — unresolved calculation inputs (never treated as zero).
const INPUT_REASON_TEXT = {
    get ABSENCE_OVERLAPPING_RECORDS() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.ABSENCE_OVERLAPPING_RECORDS", "Two absences overlap on the same day."); },
    get ABSENCE_WITH_VV_SAME_DAY() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.ABSENCE_WITH_VV_SAME_DAY", "An absence and a VV day are on the same date."); },
    get ABSENCE_TYPE_CREDIT_RULE_NOT_ESTABLISHED() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.ABSENCE_TYPE_CREDIT_RULE_NOT_ESTABLISHED", "Unpaid leave: the working-time credit rule is not established."); },
    get ANNUAL_LEAVE_WITH_WORK_SAME_DAY() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.ANNUAL_LEAVE_WITH_WORK_SAME_DAY", "Annual leave and reviewed work on the same day."); },
    get ABSENCE_WITH_WORK_NO_ROSTER() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.ABSENCE_WITH_WORK_NO_ROSTER", "Absence with work on a day without a published roster."); },
    get ROSTER_NOT_IN_FORCE_BEFORE_ABSENCE_DAY() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.ROSTER_NOT_IN_FORCE_BEFORE_ABSENCE_DAY", "The roster for an absence day was published only on or after that day."); },
    get OPEN_CLOCK_SESSION() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.OPEN_CLOCK_SESSION", "A clock session is still open."); },
    get CYCLE_BOUNDARY_SHIFT_ALLOCATION_UNRESOLVED() { return i18n.t("wf.AveragingCloseValidity.INPUT_REASON_TEXT.CYCLE_BOUNDARY_SHIFT_ALLOCATION_UNRESOLVED", "A night shift crosses the cycle boundary; its allocation is not established."); },
};

const sourceLabel = (s) =>
    `${s.restaurant_name || s.restaurant_id} ${s.start_date}–${s.end_date}: ${s.current_state.toLowerCase()}`;

export const AveragingCloseValidity = ({ closeState }) => {
    // Data arrives with GET /periods/{id}/close (no extra request).
    const data = closeState && closeState.averaging_validity;

    // SETTLED_AFTER_CLOSE is explained by the settlement panel (no action needed).
    const rows = ((data && data.employees) || []).filter(
        (e) => !["VALID", "SETTLED_AFTER_CLOSE"].includes(e.current_status));
    if (!rows.length) return null;
    return (
        <div data-testid="averaging-close-validity"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
            {rows.map((e) => (
                <div key={e.employee_id} data-testid={`averaging-validity-${e.employee_id}`}>
                    <div className="flex items-start gap-2 font-medium">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span data-testid={`averaging-validity-status-${e.employee_id}`}>
                            {e.employee_name ? `${e.employee_name}: ` : ""}{STATUS_TEXT[e.current_status]}
                        </span>
                    </div>
                    {e.recommended_action !== "CONFIRM_HOURS_THROUGH_CYCLE_END"
                        && e.source_periods.filter((s) => s.changes.length).map((s) => (
                        <div key={s.period_id} className="ml-6"
                            data-testid={`averaging-validity-source-${e.employee_id}-${s.period_id}`}>
                            {sourceLabel(s)}
                        </div>
                    ))}
                    {(e.reasons || []).filter((r) => INPUT_REASON_TEXT[r]).map((r) => (
                        <div key={r} className="ml-6" data-testid={`averaging-validity-reason-${e.employee_id}-${r}`}>
                            {INPUT_REASON_TEXT[r]}
                        </div>
                    ))}
                    {ACTION_TEXT[e.recommended_action] && (
                        <div className="ml-6" data-testid={`averaging-validity-action-${e.employee_id}`}>
                            {ACTION_TEXT[e.recommended_action]}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default AveragingCloseValidity;
